import { useRef, useState } from 'react'
import { useFakeProgress } from 'sierra-client/hooks/use-fake-progress'
import { useOnMount } from 'sierra-client/hooks/use-on-mount'
import { useOnUnmount } from 'sierra-client/hooks/use-on-unmount'
import { useTranslation } from 'sierra-client/hooks/use-translation'
import { useIsMobile } from 'sierra-client/state/browser/selectors'
import { CharactersView } from 'sierra-client/views/v3-author/scenario/character'
import { ScenarioChatModal } from 'sierra-client/views/v3-author/scenario/chat'
import { useScenarioCardActions } from 'sierra-client/views/v3-author/scenario/hooks'
import { LinesLoader } from 'sierra-client/views/v3-author/scenario/loading-views'
import { useTracking } from 'sierra-client/views/v3-author/scenario/tracking'
import { ScenarioFile } from 'sierra-client/views/v3-author/scenario/utils'
import { CreateContentId } from 'sierra-domain/api/nano-id'
import { floorToClosest } from 'sierra-domain/utils'
import { AiText, EditableText, Icon, Tooltip } from 'sierra-ui/components'
import { Button, Heading, Spacer, Text, View } from 'sierra-ui/primitives'
import { token } from 'sierra-ui/theming'
import { ConditionalWrapper } from 'sierra-ui/utils'
import styled, { css } from 'styled-components'

const LoadingPercentageText: React.FC = () => {
  const { t } = useTranslation()
  const progress = useFakeProgress({ finishDuration: 15, target: 100 })
  useOnMount(progress.startInfiniteProgress)
  useOnUnmount(progress.finishProgress)
  return (
    <View gap='4' paddingBottom='8'>
      <AiText bold>{t('scenario-card.generate.loading.simulation')}</AiText>
      <AiText>{floorToClosest({ value: progress.value, closestNumber: 3 })}%</AiText>
    </View>
  )
}

const LoadingWrapper = styled(View).attrs({
  direction: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 100%;
`
const LinesWrapper = styled(View).attrs({
  direction: 'column',
})`
  width: 100%;
`
const LoadingView: React.FC = () => {
  return (
    <LoadingWrapper direction='column' gap='40'>
      <LinesWrapper grow gap='24'>
        <View direction='column' alignItems='center'>
          <LinesLoader $width='100%' $height='32px' />
          <LinesLoader $width='80%' $height='32px' />
        </View>
        <View direction='column' alignItems='center'>
          <LinesLoader $width='90%' $height='12px' />
          <LinesLoader $width='100%' $height='12px' />
        </View>
      </LinesWrapper>
      <LoadingPercentageText />
    </LoadingWrapper>
  )
}

const PreviewWrapper = styled(View)`
  max-width: 548px;
  span {
    text-wrap: pretty;
  }
  height: 100%;
`
const ArrowWrapper = styled(View)`
  padding: 0 32px;
  width: 100%;
  height: 88px;
  justify-content: flex-end;
  display: grid;
  grid-template-areas: '. arrow';
  grid-template-columns: 1fr 1fr;
`

const StyledEditableText = styled(EditableText).attrs({ $withSingleClick: true })`
  flex: unset;
`

// TODO: these are very much temporary component until we use the proper editor instead
const EditableTitle = styled(StyledEditableText)`
  text-wrap: pretty;
  font-size: 2.5rem;
  line-height: 1.1em;
  font-weight: 500;
`
const EditableDescription = styled(StyledEditableText)`
  line-height: 1.4;
  vertical-align: baseline;
  ${p =>
    p.contentEditable &&
    css`
      color: ${token('foreground/muted')};
    `}
`
export const ScenarioPreview: React.FC<{
  file: ScenarioFile
  courseId: CreateContentId
  readOnly: boolean
  isEditing: boolean
  loading: boolean
}> = ({ loading, file, courseId, readOnly, isEditing }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const tracking = useTracking({ fileId: file.id, courseId })
  const { setAvatar, changeGenerated } = useScenarioCardActions({ file, courseId })
  const { character, scenario: _scenario } = file.data.input
  const { userContext, assistantContext, scenario: _generatedScenario } = file.data.generated ?? {}
  const [showPreview, setShowPreview] = useState(false)
  const hasGeneratedScenario = userContext !== undefined && assistantContext !== undefined
  const canPreview = !readOnly && hasGeneratedScenario && !isEditing && !loading

  const [editableTitle, setEditableTitle] = useState(false)
  const editTitleRef = useRef<HTMLParagraphElement>(null)
  const [editableDescription, setEditableDescription] = useState(false)
  const editDescriptionRef = useRef<HTMLParagraphElement>(null)

  const scenarioToShow = _generatedScenario ?? _scenario

  if (!hasGeneratedScenario && !loading) {
    return (
      <View grow direction='column' justifyContent='center' alignItems='center'>
        <PreviewWrapper grow direction='column' justifyContent='center' alignItems='center' gap='16'>
          <Heading align='center' color='form/disabled/foreground' size='h3' bold>
            {t('scenario-card.scenario-summary.title.text')}
          </Heading>
          <Text align='center' color='form/disabled/foreground' size='regular'>
            {t('scenario-card.scenario-summary.subtitle.text')}
          </Text>
          <ArrowWrapper>
            <svg
              style={{ gridArea: 'arrow' }}
              width='100%'
              height='100%'
              viewBox='0 0 471 76'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              preserveAspectRatio='none'
            >
              <path
                d='M158.334 63.9643L157.943 63.044L157.943 63.044L158.334 63.9643ZM180.747 48.9114L181.731 49.0907L181.731 49.0907L180.747 48.9114ZM172.705 41.114L172.846 40.124L172.846 40.124L172.705 41.114ZM154.33 48.1989L155.209 48.676L155.209 48.676L154.33 48.1989ZM171.473 64.8215L171.7 63.8476L171.7 63.8476L171.473 64.8215ZM470.922 5.38833C471.136 4.87938 470.897 4.29294 470.388 4.07848L462.095 0.583548C461.586 0.369082 460.999 0.607802 460.785 1.11674C460.57 1.62569 460.809 2.21213 461.318 2.42659L468.69 5.5332L465.584 12.9054C465.369 13.4143 465.608 14.0008 466.117 14.2152C466.626 14.4297 467.212 14.191 467.427 13.682L470.922 5.38833ZM1.4656 51.1349C0.976834 50.8777 0.372151 51.0655 0.115006 51.5542C-0.142139 52.043 0.0456289 52.6477 0.534398 52.9048L1.4656 51.1349ZM158.726 64.8845C160.347 64.1951 161.872 63.5291 163.302 62.8839L162.48 61.0607C161.064 61.6992 159.553 62.3594 157.943 63.044L158.726 64.8845ZM163.302 62.8839C169.26 60.1967 173.625 57.8558 176.603 55.6769C179.558 53.5143 181.311 51.3918 181.731 49.0907L179.763 48.7322C179.484 50.2643 178.25 51.993 175.421 54.063C172.615 56.1166 168.402 58.3895 162.48 61.0607L163.302 62.8839ZM181.731 49.0907C182.168 46.6892 181.667 44.6009 180.051 43.0197C178.497 41.4983 176.039 40.5776 172.846 40.124L172.565 42.1041C175.591 42.534 177.545 43.3658 178.652 44.4491C179.698 45.4726 180.108 46.8413 179.763 48.7322L181.731 49.0907ZM172.846 40.124C169.608 39.6641 165.735 39.8281 162.193 40.956C158.648 42.0853 155.358 44.2092 153.451 47.7219L155.209 48.676C156.799 45.7453 159.587 43.8849 162.8 42.8617C166.017 41.8372 169.583 41.6805 172.565 42.1041L172.846 40.124ZM153.451 47.7219C151.93 50.5238 152.134 53.445 153.798 56.102C155.427 58.7043 158.428 61.0232 162.474 62.8811L163.308 61.0635C159.453 59.2932 156.837 57.1871 155.493 55.0406C154.183 52.9488 154.057 50.7974 155.209 48.676L153.451 47.7219ZM162.474 62.8811C164.983 64.0337 167.924 65.022 171.247 65.7955L171.7 63.8476C168.487 63.0996 165.677 62.1514 163.308 61.0635L162.474 62.8811ZM171.247 65.7955C258.415 86.0845 345.273 56.8521 470.377 5.9262L469.623 4.0738C344.431 55.0355 258.158 83.9711 171.7 63.8476L171.247 65.7955ZM0.534398 52.9048C21.4176 63.8916 48.7609 72.0623 76.9613 74.9055C105.158 77.7483 134.293 75.273 158.726 64.8845L157.943 63.044C133.911 73.2623 105.137 75.7361 77.1619 72.9156C49.1907 70.0955 22.0995 61.9905 1.4656 51.1349L0.534398 52.9048Z'
                fill='black'
                fillOpacity='0.1'
              />
            </svg>
          </ArrowWrapper>
        </PreviewWrapper>
      </View>
    )
  }

  return (
    <PreviewWrapper gap='none' grow direction='column' justifyContent='center' alignItems='center'>
      <Text color='foreground/muted' bold>
        {t('scenario-card.ai-character.select-ai-character.text')}
      </Text>
      <Spacer size='12' />
      <CharactersView
        selectedId={character?.name}
        onSelect={character => {
          setAvatar({
            characterId: character.id,
            avatar: character.image,
            voice: character.voice,
          })
        }}
      />
      <Spacer size='48' />
      {loading ? (
        <LoadingView />
      ) : (
        <View direction='column' gap='16' justifyContent='center' alignItems='center' paddingBottom='32'>
          <EditableTitle
            align='center'
            value={scenarioToShow?.title ?? 'No title yet'}
            onRename={v => changeGenerated.scenarioTitle(v)}
            contentEditable={editableTitle}
            setContentEditable={setEditableTitle}
            ref={editTitleRef}
          />

          <EditableDescription
            size={isMobile ? 'small' : 'regular'}
            align='center'
            color='foreground/muted'
            contentEditable={editableDescription}
            setContentEditable={setEditableDescription}
            value={scenarioToShow?.description ?? 'No description yet'}
            onRename={v => changeGenerated.scenarioDescription(v)}
            ref={editDescriptionRef}
          />
        </View>
      )}
      <View>
        <ConditionalWrapper
          condition={!canPreview}
          renderWrapper={children => (
            <Tooltip side='top' title={t('scenario-card.buttons.preview.tooltip.text')}>
              {children}
            </Tooltip>
          )}
        >
          <Button
            disabled={!canPreview}
            variant='primary'
            onClick={() => {
              setShowPreview(true)
              tracking.misc.clickPreview()
            }}
          >
            {t('scenario-card.scenario-summary.buttons.preview-simulation')}
          </Button>
        </ConditionalWrapper>
      </View>
      <Spacer size='12' />
      <View gap='4'>
        <Icon size='size-12' iconId='locked--filled' color='foreground/muted' />
        <Text size='micro' color='foreground/muted'>
          {t('scenario-card.chat-header.private-notice')}
        </Text>
      </View>
      {showPreview && (
        <ScenarioChatModal
          open={showPreview}
          file={file}
          courseId={courseId}
          onClose={() => {
            setShowPreview(false)
          }}
          onEnd={() => {
            setShowPreview(false)
          }}
        />
      )}
    </PreviewWrapper>
  )
}
